import { useState, type FC } from 'react'
import { Step } from '~/components/CheckoutProgress'
import { PageForCheckoutFlow } from '~/components/Page'
import { Widget } from '~/components/Widget'
import { CauseSplitInput } from '~/components/CauseSplitInput'
import { MessageBox, SplitType, useCheckoutPlan, getOpenDayCount, calculateDailyAmounts, DafDisclaimer, getFirstDay, hasQurbaniCause, MTDtracking, scrollToHash, ScrollHash, getMinimalMTDAmount, totalQurbaniDonation } from '@shamaazi/mytennights'
import { EverGiveToolTipContent } from '~/components/EverGiveToolTipContent'
import { EverGivePercentageInput } from '~/components/EverGivePercentageInput'
import { TipTooltipContent } from '~/components/TipToolTipContent'
import { TipSelection } from '~/components/TipSelection'
import { AutoEnrolCheckbox } from '~/components/AutoEnrolCheckbox'
import { symbols, displayTidyWithoutCurrency, displayTidyCurrency } from '@lib/services'
import { Button, Variant } from '@lib/components'
import { useNavigate } from 'react-router-dom'
import whiteArrow from '~/assets/icons/arrow-right-white.svg'
import grayArrow from '~/assets/icons/arrow-right-gray-4.svg'
import mytendaysEvergive from '~/assets/mytendays_x_evergive.svg'
import { Navigation } from '~/navigation/navigation'
import { useAuth } from '@lib/hooks'
import { MytennightsTestId } from '@lib/testing'

export const DonationSplit: FC = () => {
  const { plan, setPlan } = useCheckoutPlan()
  const navigate = useNavigate()
  const { user } = useAuth()

  const causesTotal = Object.values(plan.causes).reduce((acc, v) => acc + v, 0)
  const qurbaniTotal = totalQurbaniDonation(plan)
  const donationTotal = causesTotal + plan.paymentDetails.tipValue

  const totalDays = getOpenDayCount(plan.startDateOffset)
  const { dailyAmount, arafahAmount } = calculateDailyAmounts(plan.split, plan.multiplier, plan.startDateOffset, donationTotal)

  const [deletedAmountError, setDeletedAmountError] = useState('')
  const [minimumAmountError, setMinimumAmountError] = useState('')

  const containsQurbaniCause = hasQurbaniCause(plan)

  const onSubmit = (): void => {
    const isValid = getMinimalMTDAmount(plan.charity.currency) <= causesTotal - qurbaniTotal
    if (isValid) {
      setPlan({ ...plan, firstNight: getFirstDay(plan.startDateOffset) })
      MTDtracking.confirmAmount(user?.email, plan)
      if (containsQurbaniCause) {
        navigate(Navigation.qurbani())
      } else {
        navigate(Navigation.extras())
      }
    } else {
      setMinimumAmountError(`Amount entered is too low, minimal amount is ${displayTidyCurrency(getMinimalMTDAmount(plan.charity.currency), plan.charity.currency)}`)
      scrollToHash(ScrollHash.lowAmountEntered)
    }
  }

  return <PageForCheckoutFlow progressBarStep={Step.Split} skinny={true} withQurbani={containsQurbaniCause}>
    <div className="x-full flex flex-col items-center gap-y-4">
      <h1 className="text-mtd-h2 font-bold text-mtd-secondary-5">How should we split your donation?</h1>

      {/* Cause input */}
      <Widget className="cause-split-bg-image bg-cover bg-no-repeat bg-center" border="border border-mtd-indigo-3" backgroundColour="">
        <h2 className="text-mtd-secondary-5 text-mtd-h3 font-bold">Your Daily Donations</h2>
        {plan.charity.charity_name !== '' &&
          <h3 className="mt-1 text-mtd-secondary-5 font-semibold text-mtd-label leading-none">to {plan.charity.charity_name}</h3>
        }

        <div className="pt-4" id={ScrollHash.lowAmountEntered}>
          <CauseSplitInput deletedAmountError={deletedAmountError} minimumAmountError={minimumAmountError} setDeletedAmountError={setDeletedAmountError} setMinimumAmountError={setMinimumAmountError}/>
        </div>
      </Widget>

      {/* EverGive contribution */}
      <Widget isEvergive>
        <div className='mt-3 flex flex-row justify-between w-full'>
          <img src={mytendaysEvergive} alt="MyTenDays x EverGive" />
            <MessageBox variant={Variant.mtd} width={6} infoIcon tooltipBorder customBoxStyle="w-tooltip-box right-0">
              <EverGiveToolTipContent />
            </MessageBox>
          </div>

        <h1 className="mt-4 text-mtd-evergive text-mtd-h4 font-bold leading-tight">How much of your donation would you like to last forever?</h1>

        <p className="text-eg-green-100 text-mtd-body-small  font-normal text-opacity-80 mt-2">
          We invest a portion of your nightly donation and give the profits to charity every month forever, so your rewards
          go on for thousands of years after you die.
        </p>
        <div className="pt-4">
          <EverGivePercentageInput />
        </div>
      </Widget>

      {/* Tip section */}
      <Widget>
      <div className='flex flex-row gap-x-5 justify-between w-full md:mb-0 relative'>
        <h1 className="text-mtd-h4 font-bold text-mtd-secondary-5">Your tip fuels our mission</h1>
        <MessageBox variant={Variant.mtd} width={6} infoIcon tooltipBorder customBoxStyle="w-tooltip-box right-0">
            <TipTooltipContent charityID={plan.charity.charity_id} />
        </MessageBox>
        </div>
        <p className=" text-mtd-secondary-5 text-mtd-body-small font-normal text-opacity-60 mt-2">
          Applied to all subscriptions
        </p>
        <div className='mt-4'>
          <TipSelection />
        </div>
      </Widget>

      {/* Auto-enrol */}
      <Widget largeBorder border={`text-mtd-secondary-5 ${plan.autoEnroll === 'true' ? 'border-2 border-mtd-blue-1' : 'border'}`}>
        <h2 className="text-mtd-h3 font-bold">Never Miss the 10 Days of Dhul Hijjah</h2>
        <p className="text-mtd-body mt-4">
          Catch the blessings of The 10 Days Dhul Hijjah over and over! MyTenDays will automatically take your donations each year.
          <strong> Cancel or edit anytime</strong>
        </p>
          <AutoEnrolCheckbox />
      </Widget>

      {/* Donation breakdown */}
      <Widget radius="rounded-2xl" backgroundColour="bg-fg-blue-320">
        {/* Total donation amount */}
        <div className="flex flex-row justify-between text-white leading-none">
          <p className='text-mtd-h4 font-bold'>Total</p>
          <p>
            <span className='text-mtd-h4 font-normal mr-1'>{symbols[plan.charity.currency]}</span>
            <span className='text-mtd-h2 font-bold'>{displayTidyWithoutCurrency(donationTotal)}</span>
          </p>
        </div>

        {/* Daily donation amount */}
        <div className="mt-4 flex flex-row justify-between text-mtd-indigo-3 leading-none">
          <p className='text-mtd-info font-medium'>Equal split ({plan.split === SplitType.MoreArafah ? totalDays - 1 : totalDays} days)</p>
          <p>
            <span className='text-mtd-info font-normal mr-1'>{symbols[plan.charity.currency]}</span>
            <span className='text-mtd-label font-semibold'>{displayTidyWithoutCurrency(dailyAmount)}</span>
          </p>
        </div>

        {/* Arafah donation amount */}
        {plan.split === SplitType.MoreArafah &&
          <div className="mt-1.5 flex flex-row justify-between text-mtd-indigo-3 leading-none">
            <p className='text-mtd-info font-medium'>on Arafah</p>
            <p>
              <span className='text-mtd-info font-normal mr-1'>{symbols[plan.charity.currency]}</span>
              <span className='text-mtd-label font-semibold'>{displayTidyWithoutCurrency(arafahAmount)}</span>
            </p>
          </div>
        }
      </Widget>

      {deletedAmountError && <Widget className='py-3' backgroundColour='mtd-beige-1' border="border-0">
         <p className="text-mtd-red text-mtd-info font-normal">{deletedAmountError}</p>
      </Widget>}

      <Button data-test-id={MytennightsTestId.continueButton} disabled={causesTotal === 0} className="w-full flex flex-row items-center justify-center gap-x-2 font-bold" variant='mtd-primary' onClick={onSubmit}>
        <div>CONTINUE</div> <img src={causesTotal === 0 ? grayArrow : whiteArrow} alt="right arrow" />
      </Button>

      <div>
        <DafDisclaimer country={plan.charity.country} product="MTD" charityId={plan.charity.charity_id}/>
      </div>

    </div>

  </PageForCheckoutFlow>
}
